/**
 * SHELL — the fixed-timestep run loop.
 *
 * Determinism contract (CLAUDE.md): the sim advances at FIXED_DT (60 Hz) ONLY,
 * never at the render frame rate. We accumulate real elapsed time and step the
 * Simulation in whole fixed ticks; the leftover fraction becomes `alpha` in
 * [0,1), handed to renderer.render(state, alpha) for smooth interpolation on
 * high-refresh displays.
 *
 * Each tick we sample the current steer from the InputController and feed the RAW
 * steer to sim.step (Core quantizes to int16 internally for the replay).
 *
 * Swapping the fake sim for @/core's real one is a one-line change at the call
 * site — this loop only depends on the `Simulation` + `Renderer` interfaces.
 */

import { FIXED_DT_MS, MAX_RUN_TICKS } from '@/types';
import type { GameState } from '@/types';
import type { Simulation, SteerInput } from '@/core';
import type { Renderer } from '@/render';
import type { InputController } from './input';

export interface LoopHandle {
  /** Stop the loop (does not dispose sim/renderer). */
  stop(): void;
  /** True while running. */
  readonly running: boolean;
}

export interface LoopOptions {
  readonly sim: Simulation;
  readonly renderer: Renderer;
  readonly input: InputController;
  /** Called after each fixed tick with the fresh state (HUD updates). */
  readonly onTick?: (state: GameState) => void;
  /** Called once when the run leaves 'running' (finished or dnf). */
  readonly onFinish?: (state: GameState) => void;
  /** Cap to avoid spiral-of-death after a long tab stall. Default 5 ticks. */
  readonly maxCatchUpTicks?: number;
}

/** Start the run loop immediately. Returns a handle to stop it. */
export function startRunLoop(opts: LoopOptions): LoopHandle {
  const { sim, renderer, input, onTick, onFinish } = opts;
  const maxCatchUp = opts.maxCatchUpTicks ?? 5;

  let running = true;
  let finished = false;
  let rafId = 0;
  let last = performance.now();
  let accumulator = 0;
  let latest: GameState = sim.getState();

  const finish = (state: GameState): void => {
    if (finished) return;
    finished = true;
    running = false;
    cancelAnimationFrame(rafId);
    onFinish?.(state);
  };

  const frame = (now: number): void => {
    if (!running) return;
    rafId = requestAnimationFrame(frame);

    let delta = now - last;
    last = now;
    // Clamp pathological deltas (tab switch, breakpoint) to avoid huge catch-up.
    if (delta > FIXED_DT_MS * maxCatchUp) delta = FIXED_DT_MS * maxCatchUp;
    accumulator += delta;

    let steps = 0;
    while (accumulator >= FIXED_DT_MS && steps < maxCatchUp) {
      const raw: SteerInput = input.steer; // RAW [-1,1]; Core quantizes
      latest = sim.step(raw);
      accumulator -= FIXED_DT_MS;
      steps += 1;
      onTick?.(latest);

      if (latest.status !== 'running' || latest.tick >= MAX_RUN_TICKS) {
        const alpha = 0;
        renderer.render(latest, alpha);
        finish(latest);
        return;
      }
    }

    const alpha = accumulator / FIXED_DT_MS; // [0,1)
    renderer.render(latest, alpha);
  };

  rafId = requestAnimationFrame(frame);

  return {
    stop() {
      running = false;
      cancelAnimationFrame(rafId);
    },
    get running() {
      return running;
    },
  };
}
